import type { Product } from "@models/product";
import { isSellable } from "./selectVendorSku";

/** The vendor the shopper chose under "Purchase from another vendor" (B-09). */
export interface ChosenVendor {
    productId: string;
    vendorSkuId: string;
    vendorName: string;
}

export interface VendorChoiceState {
    // null: the page buys from the cheapest sellable offering.
    chosen: ChosenVendor | null;
    // The vendor a re-read took away, named in the page's notice until the shopper's next
    // choice or variant change.
    goneVendorName: string | null;
}

export const NO_VENDOR_CHOICE: VendorChoiceState = {
    chosen: null,
    goneVendorName: null,
};

/**
 * The choice after a product read (review finding 4). Every read clears it — a new product, or
 * the same one re-read for a new ship-to destination — so a choice never silently comes back.
 * When the SAME product's re-read no longer lets the chosen vendor sell, the page says so rather
 * than changing the vendor and price with nothing on screen saying why.
 */
export const choiceAfterRead = (
    prev: VendorChoiceState,
    product: Product,
): VendorChoiceState => {
    const chosen = prev.chosen;

    if (chosen === null || chosen.productId !== product.id) {
        return NO_VENDOR_CHOICE;
    }

    const rows = [
        ...product.vendors,
        ...product.variants.flatMap((variant) => variant.vendors ?? []),
    ];
    const stillSells = rows.some(
        (row) => row.id === chosen.vendorSkuId && isSellable(row),
    );

    return {
        chosen: null,
        goneVendorName: stillSells ? null : chosen.vendorName,
    };
};
